import {
  emailLinkedPaths,
  IAdminWorkshiftDetails,
  IBroadcastNavigationModel,
  IBroadcastNotificationsData,
  NavigateToType,
  TimeFormat,
} from '@pharmaplan/common';

import { ReplacementLabels } from '../../../../../../helpers/Constants';
import {
  buildLandingUrl,
  parseAbsoluteUrl,
  timeString,
} from '../../../../../../helpers/Functions';
import { getDayMonthDateYear } from '../../../../../Reports/helpers';

const { viewMoreShift, workshiftDetails } = emailLinkedPaths;
const { ShiftURL, Pharmacy } = NavigateToType;

const idQuery = 'id';
const isExternalQuery = 'isExternal';

export interface IBroadcastNavigationValues {
  navigateTo: NavigateToType | '';
  shiftUrl: string;
  pharmacyId: string;
}

export interface IBroadcastNavigationFormValues
  extends IBroadcastNavigationValues {
  pharmacyUrl: string;
  shiftDetails: string;
}

export interface IShiftUrlParams {
  id: string;
  isExternal: boolean;
}

export const toPharmacyUrl = (pharmacyId: string) =>
  (pharmacyId ? buildLandingUrl(viewMoreShift, { [idQuery]: pharmacyId }) : '');

// SHIFT IDENTITY THE PASTED URL POINTS AT, NULL WHEN IT IS NOT A SHIFT LINK
export const toShiftUrlParams = (shiftUrl: string): IShiftUrlParams | null => {
  const parsedUrl = parseAbsoluteUrl(shiftUrl.trim());

  if (parsedUrl?.pathname !== workshiftDetails) {
    return null;
  }

  const id = parsedUrl.searchParams.get(idQuery);

  if (!id) {
    return null;
  }

  return {
    id,
    isExternal: parsedUrl.searchParams.get(isExternalQuery) === 'true',
  };
};

export const emptyNavigationModel: IBroadcastNavigationModel = {
  navigation: null,
  navigateTo: null,
  navigationId: null,
  isExternal: false,
};

// REBUILDS THE FORM STATE OF A NOTIFICATION THAT IS BEING RESENT
export const toNavigationValues = (
  notification: IBroadcastNotificationsData,
): IBroadcastNavigationFormValues => {
  const { navigateTo, navigation, navigationId } = notification
    ?? emptyNavigationModel;

  const emptyValues = {
    navigateTo: '' as const,
    shiftUrl: '',
    pharmacyId: '',
    pharmacyUrl: '',
    shiftDetails: '',
  };

  if (navigateTo === Pharmacy) {
    const pharmacyId = navigationId ?? '';

    return {
      ...emptyValues,
      navigateTo: Pharmacy,
      pharmacyId,
      pharmacyUrl: navigation ?? toPharmacyUrl(pharmacyId),
    };
  }

  if (navigateTo === ShiftURL) {
    return { ...emptyValues, navigateTo: ShiftURL, shiftUrl: navigation ?? '' };
  }

  return emptyValues;
};

export const toNavigationModel = ({
  navigateTo,
  shiftUrl,
  pharmacyId,
}: IBroadcastNavigationValues): IBroadcastNavigationModel => {
  if (navigateTo === ShiftURL) {
    const shiftParams = toShiftUrlParams(shiftUrl);

    return {
      navigation: shiftParams ? shiftUrl.trim() : null,
      navigateTo: ShiftURL,
      navigationId: shiftParams?.id ?? null,
      isExternal: shiftParams?.isExternal ?? false,
    };
  }

  if (navigateTo === Pharmacy) {
    return {
      navigation: toPharmacyUrl(pharmacyId) || null,
      navigateTo: Pharmacy,
      navigationId: pharmacyId || null,
      isExternal: false,
    };
  }

  return emptyNavigationModel;
};

export const hasIncompleteNavigation = ({
  navigateTo,
  shiftUrl,
  pharmacyId,
}: IBroadcastNavigationValues) => {
  if (navigateTo === ShiftURL) {
    return !toShiftUrlParams(shiftUrl);
  }

  return navigateTo === Pharmacy && !pharmacyId;
};

// PHARMACY NAME (DATE | TIME | REPLACEMENT TYPE) FOR THE READ ONLY FIELD
export const toShiftDetailsLabel = (
  details: IAdminWorkshiftDetails,
  timeFormat: TimeFormat,
) => {
  const { pharmacyName, startDate, endDate, replacementType } = details ?? {};

  const date = getDayMonthDateYear(startDate);
  const time = timeString(startDate, endDate, timeFormat);
  const replacement = ReplacementLabels()[replacementType];

  return `${pharmacyName} (${date} | ${time} | ${replacement})`;
};
